import React, { useId, forwardRef, Ref } from 'react';

// Styled Components
import { Container, Label, Area } from './styles';
import { ErrorMessage } from '../InputText/styles';

// Types
import { AreaProps } from './types';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';

/**
 * Textarea compartido — componente TRANSPARENTE, igual que su hermano
 * `InputText`: no guarda el valor en estado propio, sólo pinta label/error y
 * deja pasar `value`, `onChange`, `placeholder`… al `<textarea>` vía `{...props}`.
 * Así sirve tanto a RHF `register` (uncontrolled) como a `Controller`
 * (controlled) sin saber cuál usa el consumidor.
 *
 * Fix 2026-07-16 (bug reportado: "no me deja escribir" en los modales de RV):
 * antes duplicaba el valor en un `useState` interno que sólo actualizaba su
 * handler propio `onChangeValue`. Como `{...props}` se esparce DESPUÉS de
 * `onChange={onChangeValue}`, el `onChange` del consumidor lo sobrescribía y
 * `setTextValue` no se llamaba jamás → `value={textValue}` quedaba congelado en
 * el valor inicial. Con `Controller` (re-render por tecla) React revertía lo
 * escrito; con `register` (sin re-render) el bug quedaba latente. Eliminar el
 * estado interno es lo que alinea el componente con `InputText`.
 */
export const InputTextArea = forwardRef(
  (
    {
      label,
      disabled,
      name,
      error,
      borderError,
      className,
      ...props
    }: AreaProps,
    ref: Ref<HTMLTextAreaElement>
  ): JSX.Element => {
    const id = useId();

    const isLandscapeTablet = useIsLandscapeTablet();

    return (
      <Container className={className}>
        {label && (
          <Label
            size={isLandscapeTablet ? 'XS' : 'S'}
            variant="regular"
            component="label"
            htmlFor={`${id}-${name}`}
          >
            {label}
          </Label>
        )}
        <Area
          disabled={disabled}
          id={`${id}-${name}`}
          name={name}
          spellCheck={true}
          autoComplete="on"
          ref={ref}
          borderError={borderError}
          {...props}
        />
        {error && (
          <ErrorMessage colorText={'lightCoralRed'} size={'XXS'}>
            {error}
          </ErrorMessage>
        )}
      </Container>
    );
  }
);
